// custom-grid.component.ts
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { LedGridEditorComponent } from './led-grid-editor/led-grid-editor.component';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { LedBoardService } from '../../services/led-board.service';
import { ImageImportService } from '../../services/image-import.service';
import { FitMode, adjustFrame } from '../../models/led-image.util';
import { AnimationFrame, Frame, HexColor, emptyFrame, LedDesign } from '../../models/led-board.model';
import { buildFrameDesign, buildAnimationDesign, buildFramePayload, buildAnimationPayload, compressAnimationPayload, compressFramePayload, previewFrameFromPayload } from '../../models/led-board.util';
import { Subscription, interval } from 'rxjs';
import { QueueEntry, NowShowing, DEFAULT_HOLD_MS } from '../../models/led-queue.model';
import {
  SessionMeta, SessionPerson, sessionIsLive, sessionRemainingMs, countdownText,
  frameFromPixels, pixelDiff, colorForClient, newClientId, DEFAULT_SESSION_MS,
  SESSIONS_ENABLED,
} from '../../models/led-session.model';

type Tool = 'pencil' | 'eraser' | 'eyedropper' | 'fill' | 'row' | 'col' | 'line' | 'rect' | 'circle';
/** One thing on screen at a time: an empty grid is a terrible first impression. */
export type StudioView = 'start' | 'draw' | 'gallery' | 'queue' | 'session';

@Component({
  selector: 'app-custom-grid',
  templateUrl: './custom-grid.component.html',
  styleUrls: ['./custom-grid.component.scss'],
  standalone: false,
})
export class CustomGridComponent implements OnInit, OnDestroy {
  view: StudioView = 'start';

  /** A picture of what the board is showing, for the start screen. */
  livePreview: string | null = null;
  liveAnimated = false;
  private liveSub?: Subscription;

  // The line. The board runs it, so a phone can be pocketed the moment a
  // design is queued; these are just what everyone else can see of it.
  queue: QueueEntry[] = [];
  nowShowing: NowShowing | null = null;
  myQueueKey: string | null = null;
  myThumbnail: string | null = null;
  /** Whether what is in the line came from the editor, and so is worth offering to save. */
  myIsEditorWork = true;
  clock = Date.now();
  private queueSub?: Subscription;
  private nowSub?: Subscription;
  private clockSub?: Subscription;

  get boardIsBusy(): boolean { return this.queue.length > 0 || !!this.nowShowing; }

  // ---- live sessions ----
  // Everyone paints one canvas at once. There is no undo here: with several
  // hands on the same pixels, undo would take back work that isn't yours.
  session: SessionMeta | null = null;
  sessionPeople: Record<string, SessionPerson> = {};
  private sessionPixels: Record<string, string> = {};
  readonly clientId = newClientId();
  get myColor(): string { return colorForClient(this.clientId); }
  private sessionSub?: Subscription;
  private pixelsSub?: Subscription;
  private peopleSub?: Subscription;
  private inSession = false;

  readonly sessionsEnabled = SESSIONS_ENABLED;
  get sessionLive(): boolean { return this.sessionsEnabled && sessionIsLive(this.session, this.clock); }
  get sessionCountdown(): string { return countdownText(sessionRemainingMs(this.session, this.clock)); }
  get sessionPeopleCount(): number { return Object.keys(this.sessionPeople).length; }
  /** Everyone else's cursors, so the canvas can show where they are working. */
  get otherCursors(): { color: string; cell: number }[] {
    return Object.keys(this.sessionPeople)
      .filter(id => id !== this.clientId)
      .map(id => this.sessionPeople[id])
      .filter(p => p && typeof p.cell === 'number')
      .map(p => ({ color: p.color, cell: p.cell as number }));
  }
  frames: AnimationFrame[] = [{ ms: 120, pixels: emptyFrame() }];
  activeIndex = 0;

  get frame(): Frame { return this.frames[this.activeIndex].pixels; }
  set frame(f: Frame) {
    const next = this.frames.slice();
    next[this.activeIndex] = { ...next[this.activeIndex], pixels: f };
    this.frames = next;
  }
  color: HexColor = '#00ccff';
  tool: Tool = 'pencil';
  brightness = 15;
  recentColors: HexColor[] = ['#ffffff', '#ff0000', '#00ff00', '#0000ff'];
  private undoStack: { index: number; frame: Frame }[] = [];
  private redoStack: { index: number; frame: Frame }[] = [];
  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }

  // Zoom is driven from the bar rather than from buttons floating over the
  // drawing — the canvas is small enough on a phone without controls on top.
  zoomIn() { this.editor?.zoomBy(1.5); }
  zoomOut() { this.editor?.zoomBy(1 / 1.5); }
  fitBoard() { this.editor?.fit(); }
  get zoomed(): boolean { return this.editor?.zoomed ?? false; }
  get canZoomIn(): boolean { return this.editor?.canZoomIn ?? true; }
  get zoomLabel(): string { return this.editor?.zoomLabel ?? ''; }

  // The timeline is clutter for the many people who only want one picture, so
  // it stays out of the way until someone asks for an animation — or opens one.
  animating = false;
  get showTimeline(): boolean { return this.animating || this.frames.length > 1; }

  /** Reveal the timeline with a second frame carried over from the first. */
  startAnimating() {
    this.animating = true;
    this.frames = [...this.frames, { ms: 120, pixels: this.frame.slice() }];
    this.activeIndex = this.frames.length - 1;
  }

  /** Only offered while there is a single frame, so nothing is lost. */
  stopAnimating() {
    if (this.frames.length > 1) { return; }
    this.animating = false;
  }
  copiedFrame: Frame | null = null;
  sending = false;

  designs: LedDesign[] = [];
  private designsSub?: Subscription;
  saveName = '';

  loop = true;
  playing = false;
  private previewTimer: any = null;

  selectMode = false;
  toggleSelect() { this.selectMode = !this.selectMode; }
  deselect() { this.editor?.clearSelection(); }

  fitMode: FitMode = 'cover';
  imgBrightness = 0;
  imgContrast = 0;
  imgSaturation = 0;
  cropX = 0;
  cropY = 0;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild(LedGridEditorComponent) editor?: LedGridEditorComponent;
  private lastImage: HTMLImageElement | null = null;
  get hasImage(): boolean { return !!this.lastImage; }

  /**
   * Signed in means "this is the board's owner": the only person who gets to
   * take other people's designs out of the line.
   */
  isOwner = false;
  private authSub?: Subscription;

  constructor(public svc: LedBoardService, private imgSvc: ImageImportService,
              private auth?: AngularFireAuth) {}

  ngOnInit() {
    this.designsSub = this.svc.listDesigns().subscribe(d => this.designs = d);
    // Showing the board's current artwork is what tells a first-timer what this
    // page is for, before they have read a word of it.
    this.authSub = this.auth?.authState.subscribe(user => this.isOwner = !!user);
    this.queueSub = this.svc.watchQueue().subscribe(q => this.queue = q);
    this.nowSub = this.svc.watchNowShowing().subscribe(n => this.nowShowing = n);
    // One ticking clock for every countdown on the page.
    this.clockSub = interval(1000).subscribe(() => this.clock = Date.now());
    this.sessionSub = this.svc.watchSession().subscribe(s => this.onSessionChange(s));
    this.peopleSub = this.svc.watchSessionPeople().subscribe(p => this.sessionPeople = p || {});
    this.pixelsSub = this.svc.watchSessionPixels().subscribe(px => {
      this.sessionPixels = px || {};
      // In a session the shared canvas IS the drawing: take it as it comes.
      if (this.view === 'session') { this.frames = [{ ms: 120, pixels: frameFromPixels(this.sessionPixels) }]; }
    });
    this.liveSub = this.svc.watchLive().subscribe(async payload => {
      this.liveAnimated = payload?.mode === 'animation';
      const frame = await previewFrameFromPayload(payload ?? null);
      this.livePreview = frame ? this.svc.makeThumbnail(frame) : null;
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    if (this.inSession) { this.svc.leaveSession(this.clientId); }
    this.sessionSub?.unsubscribe();
    this.pixelsSub?.unsubscribe();
    this.peopleSub?.unsubscribe();
    this.liveSub?.unsubscribe();
    this.designsSub?.unsubscribe();
    this.queueSub?.unsubscribe();
    this.nowSub?.unsubscribe();
    this.clockSub?.unsubscribe();
    this.stopPreview();
  }

  goStart() { this.stopPreview(); this.leaveSessionIfIn(); this.view = 'start'; }

  /** Queue a live session — it waits its turn like everything else. */
  async startSession(durationMs = DEFAULT_SESSION_MS) {
    if (!this.sessionsEnabled || this.sending) { return; }
    this.sending = true;
    try {
      this.myIsEditorWork = false;
      this.myThumbnail = null;
      this.myQueueKey = await this.svc.joinQueueAsSession(durationMs);
      this.view = 'queue';
    } finally {
      this.sending = false;
    }
  }

  /** Pick up a brush in the session that is running now. */
  async joinSession() {
    if (!this.sessionsEnabled || !this.sessionLive) { return; }
    this.frames = [{ ms: 120, pixels: frameFromPixels(this.sessionPixels) }];
    this.activeIndex = 0;
    this.animating = false;
    this.view = 'session';
    this.inSession = true;
    await this.svc.enterSession(this.clientId, this.myColor);
  }

  leaveSessionIfIn() {
    if (!this.inSession) { return; }
    this.inSession = false;
    this.svc.leaveSession(this.clientId);
  }

  /**
   * A stroke inside a session. Only the pixels that changed are published, so
   * several people painting at once merge rather than clobber each other.
   */
  onSessionFrameChange(next: Frame) {
    const before = frameFromPixels(this.sessionPixels);
    const changes = pixelDiff(before, next);
    this.frames = [{ ms: 120, pixels: next }];
    const keys = Object.keys(changes);
    if (!keys.length) { return; }
    this.svc.paintSession(changes);
    this.svc.markSessionCell(this.clientId, Number(keys[keys.length - 1]));
  }

  /** The board opened or closed a session; follow it. */
  private onSessionChange(s: SessionMeta | null) {
    const wasId = this.session?.id;
    this.session = s;
    if (!s && this.view === 'session') {
      // Time's up. What everyone made stays on the board; we step back out.
      this.leaveSessionIfIn();
      this.view = 'start';
    }
    // Our own queued session just started: walk straight into it.
    if (s && s.id && s.id === this.myQueueKey && wasId !== s.id && this.view === 'queue') {
      this.joinSession();
    }
  }
  backToDraw() { this.view = 'draw'; }
  startDraw() { this.view = 'draw'; }
  openGallery() { this.view = 'gallery'; }

  /** Straight into the photo flow: open the editor, then the camera roll. */
  startPhoto() {
    this.view = 'draw';
    setTimeout(() => this.triggerImport());   // the file input only exists once the editor is rendered
  }

  /** Gallery handoff: open a saved design in the editor rather than on the board. */
  editDesign(design: LedDesign) {
    this.load(design);
    this.view = 'draw';
  }

  setTool(t: Tool) { this.tool = t; }
  setLoop(v: boolean) { this.loop = v; }

  pickColor(hex: HexColor) { this.color = hex; }
  recordColor(hex: HexColor) {
    this.recentColors = [hex, ...this.recentColors.filter(c => c !== hex)].slice(0, 12);
  }
  onColorPicked(hex: HexColor) { this.pickColor(hex); }

  // Fired by the editor on stroke-start: snapshot for undo, and record the
  // colour to the palette only when it's actually being drawn (not the eraser).
  onStrokeStart() {
    this.pushUndo();
    if (this.tool !== 'eraser') { this.recordColor(this.color); }
  }
  onFrameChange(f: Frame) { this.frame = f; }

  pushUndo() {
    this.undoStack.push({ index: this.activeIndex, frame: this.frame.slice() });
    this.redoStack = [];
    if (this.undoStack.length > 50) { this.undoStack.shift(); }
  }
  undo() {
    const prev = this.undoStack.pop();
    if (prev) {
      this.redoStack.push({ index: this.activeIndex, frame: this.frame.slice() });
      this.activeIndex = Math.min(prev.index, this.frames.length - 1);
      this.frame = prev.frame;
    }
  }
  redo() {
    const next = this.redoStack.pop();
    if (next) {
      this.undoStack.push({ index: this.activeIndex, frame: this.frame.slice() });
      this.activeIndex = Math.min(next.index, this.frames.length - 1);
      this.frame = next.frame;
    }
  }

  onNudge(dx: number, dy: number) {
    this.pushUndo();
    this.editor?.nudge(dx, dy);
  }

  copyFrame() { this.copiedFrame = this.frame.slice(); }
  pasteFrame() {
    if (!this.copiedFrame) { return; }
    this.pushUndo();
    this.frame = this.copiedFrame.slice();
  }

  clearBoard() { this.pushUndo(); this.frame = emptyFrame(); }

  triggerImport() { this.fileInput?.nativeElement.click(); }

  async onImportFile(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) { return; }
    try {
      this.lastImage = await this.imgSvc.fileToImage(file);
      this.applyImport();
    } catch {
      // unreadable / unsupported image — nothing applied, state unchanged
    } finally {
      input.value = '';   // reset so the same file can be re-picked
    }
  }

  applyImport() {
    if (!this.lastImage) { return; }
    this.imgBrightness = 0;
    this.imgContrast = 0;
    this.imgSaturation = 0;
    this.cropX = 0;
    this.cropY = 0;
    this.pushUndo();          // one undo entry for the whole import
    this.renderImport();
  }

  // Re-render the imported photo with the current fit/crop/adjust settings.
  // No undo push — the initial applyImport() already recorded one step.
  renderImport() {
    if (!this.lastImage) { return; }
    const base = this.imgSvc.imageToFrame(this.lastImage, this.fitMode, { x: this.cropX, y: this.cropY });
    this.frame = adjustFrame(base, {
      brightness: this.imgBrightness,
      contrast: this.imgContrast,
      saturation: this.imgSaturation,
    });
  }

  resetImport() {
    this.imgBrightness = 0;
    this.imgContrast = 0;
    this.imgSaturation = 0;
    this.cropX = 0;
    this.cropY = 0;
    this.renderImport();
  }

  setFitMode(m: FitMode) {
    this.fitMode = m;
    this.renderImport();
  }

  setImgBrightness(v: number) { this.imgBrightness = v; this.renderImport(); }
  setImgContrast(v: number) { this.imgContrast = v; this.renderImport(); }
  setImgSaturation(v: number) { this.imgSaturation = v; this.renderImport(); }
  setCropX(v: number) { this.cropX = v; this.renderImport(); }
  setCropY(v: number) { this.cropY = v; this.renderImport(); }

  save() {
    const name = this.saveName.trim();
    if (!name) { return; }
    if (this.frames.length > 1) {
      const thumb = this.svc.makeThumbnail(this.frames[0].pixels);
      this.svc.saveDesign(buildAnimationDesign(name, this.frames, this.loop, thumb, Date.now()));
    } else {
      const thumb = this.svc.makeThumbnail(this.frame);
      this.svc.saveDesign(buildFrameDesign(name, this.frame, thumb, Date.now()));
    }
    this.saveName = '';
  }
  load(design: LedDesign) {
    // Loading any design is a fresh "open": replace the whole frame set so a
    // previously-loaded animation never leaves stale frames behind. The per-frame
    // undo can't reverse a wholesale swap, so clear the stacks.
    this.stopPreview();
    this.activeIndex = 0;
    this.undoStack = [];
    this.redoStack = [];
    this.lastImage = null;
    this.fitMode = 'cover';
    this.imgBrightness = 0;
    this.imgContrast = 0;
    this.imgSaturation = 0;
    this.cropX = 0;
    this.cropY = 0;
    if (design.type === 'animation' && design.animation) {
      this.frames = design.animation.frames.map(f => ({ ms: f.ms, pixels: f.pixels.slice() }));
      this.loop = design.animation.loop;
      this.animating = this.frames.length > 1;
    } else {
      this.frames = [{ ms: 120, pixels: (design.frame || emptyFrame()).slice() }];
      this.animating = false;
    }
  }
  remove(design: LedDesign) { if (design.key) { this.svc.deleteDesign(design.key); } }

  /**
   * Put a saved design on the board without opening it first. The gallery is
   * the quickest path to a lit-up board for someone who has never drawn here,
   * so it must not disturb whatever they have in the editor.
   */
  async sendDesign(design: LedDesign) {
    if (this.sending) { return; }
    const now = Date.now();
    if (design.type === 'animation' && design.animation?.frames?.length) {
      const raw = buildAnimationPayload(design.animation.frames, design.animation.loop, this.brightness, now);
      await this.queueUp(await compressAnimationPayload(raw), design.thumbnail, design.name, false);
    } else {
      const raw = buildFramePayload(design.frame || emptyFrame(), this.brightness, now);
      await this.queueUp(await compressFramePayload(raw), design.thumbnail, design.name, false);
    }
  }

  async sendToBoard() {
    const raw = buildFramePayload(this.frame, this.brightness, Date.now());
    await this.queueUp(await compressFramePayload(raw), this.svc.makeThumbnail(this.frame), this.saveName.trim());
  }

  sendActive() {
    if (this.frames.length > 1) { this.sendAnimation(); }
    else { this.sendToBoard(); }
  }

  async sendAnimation() {
    if (this.sending) { return; }
    const raw = buildAnimationPayload(this.frames, this.loop, this.brightness, Date.now());
    const payload = await compressAnimationPayload(raw);
    await this.queueUp(payload, this.svc.makeThumbnail(this.frames[0].pixels), this.saveName.trim());
  }

  /**
   * Join the line and switch to the status screen. Everything goes through the
   * queue, even when nothing is waiting — the board promotes an empty queue
   * immediately, so "no line" and "a line" are the same code path.
   */
  private async queueUp(payload: any, thumbnail: string, name: string, fromEditor = true) {
    if (this.sending) { return; }
    this.sending = true;
    try {
      this.myThumbnail = thumbnail;
      this.myIsEditorWork = fromEditor;
      this.myQueueKey = await this.svc.joinQueue(payload, { name: name || undefined, thumbnail, holdMs: DEFAULT_HOLD_MS });
      this.view = 'queue';
    } finally {
      this.sending = false;
    }
  }

  /** Give up a spot. The design stays in the editor, so it can be sent again. */
  async leaveLine() {
    const key = this.myQueueKey;
    if (!key) { return; }
    this.myQueueKey = null;
    await this.svc.leaveQueue(key);
  }

  /** Owner only: take someone else's design out of the line. */
  async removeFromQueue(key: string) {
    if (!this.isOwner || !key) { return; }
    if (key === this.myQueueKey) { this.myQueueKey = null; }
    await this.svc.leaveQueue(key);
  }

  /** Owner only: empty the line in one go. */
  async clearQueue() {
    if (!this.isOwner) { return; }
    const keys = this.queue.map(q => q.key).filter((k): k is string => !!k);
    this.myQueueKey = null;
    await Promise.all(keys.map(k => this.svc.leaveQueue(k)));
  }

  /** Save from the queue screen, where naming is an afterthought rather than a gate. */
  saveFromQueue(name: string) {
    this.saveName = name;
    this.save();
  }

  togglePreview() {
    if (this.playing) { this.stopPreview(); return; }
    if (this.frames.length < 2) { return; }
    this.playing = true;
    let i = 0;
    const step = () => {
      this.activeIndex = i % this.frames.length;
      this.previewTimer = setTimeout(() => { i++; if (this.playing) { step(); } }, this.frames[this.activeIndex].ms);
    };
    step();
  }
  stopPreview() { this.playing = false; if (this.previewTimer) { clearTimeout(this.previewTimer); } }
}
